import { TAction } from '../../../common/types';
import { ELanguageStatus, EUserGroupType, EUserLevel } from '../general/types';
import { TConferenceSaveLevel } from '../../../common/rest/conference/conferenceSaveLevel';

export const SET_GROUPS = 'MODELS/SET_GROUPS';
export const SET_GROUPS_LOADED = 'MODELS/SET_GROUPS_LOADED';
export const ADD_GROUP = 'MODELS/ADD_GROUP';
export const REFRESH_CALL_GROUP = 'MODELS/REFRESH_CALL_GROUP';
export const REMOVE_GROUP = 'MODELS/REMOVE_GROUP';
export const ADD_AUDIO_IN_GROUP = 'MODELS/ADD_AUDIO_IN_GROUP';
export const UPDATE_AUDIO = 'MODELS/UPDATE_AUDIO';
export const REMOVE_AUDIO_FROM_GROUP = 'MODELS/REMOVE_AUDIO_FROM_GROUP';
export const MOVE_AUDIO_TO_GROUP = 'MODELS/MOVE_AUDIO_TO_GROUP';
export const SET_ACTIVE_GROUP = 'MODELS/SET_ACTIVE_GROUP';
export const SET_LANGUAGES = 'MODELS/SET_LANGUAGES';
export const SET_LANGS_ACTION = 'MODELS/SET_LANGS_ACTION';
export const SET_USER_ROLES_ACTION = 'MODELS/SET_USER_ROLES_ACTION';
export const RESET_MODELS_ACTION = 'MODELS/RESET_MODELS_ACTION';

export interface IModelsState {
  groups: TUserGroup[];
  groupsLoaded: boolean;
  languages: TUserLanguage[];
  activeGroupId: number | null;
  langs: TLanguage[];
  userRoles: TUserRole[];
}

export interface ISetGroups extends TAction<typeof SET_GROUPS> {
  groups: TUserGroup[];
}

export interface IGroupsLoadedAction extends TAction<typeof SET_GROUPS_LOADED> {
  groupsLoaded: boolean;
}

export interface ISetLanguages extends TAction<typeof SET_LANGUAGES> {
  languages: TUserLanguage[]
}

export interface ISetActiveGroupId extends TAction<typeof SET_ACTIVE_GROUP> {
  activeGroupId: number | null;
}

export interface IAddGroup extends TAction<typeof ADD_GROUP> {
  group: TUserGroup;
}

export interface IRefreshCallGroupAction extends TAction<typeof REFRESH_CALL_GROUP> {
  group: TUserGroup;
}

export interface IRemoveGroup extends TAction<typeof REMOVE_GROUP> {
  groupId: number;
}

export interface IAddAudioInGroup extends TAction<typeof ADD_AUDIO_IN_GROUP> {
  groupId: number;
  audio: TAudio;
}

export interface IUpdateAudioAction extends TAction<typeof UPDATE_AUDIO> {
  groupId: number,
  audioId: number,
  data: Partial<TAudio>
}

export interface IMoveAudioToGroup extends TAction<typeof MOVE_AUDIO_TO_GROUP> {
  audioId: number;
  fromGroupId: number;
  toGroupId: number;
}

export interface IRemoveAudioFromGroup extends TAction<typeof REMOVE_AUDIO_FROM_GROUP> {
  groupId: number;
  audioId: number;
}

export interface ISetLangsAction extends TAction<typeof SET_LANGS_ACTION> {
  langs: TLanguage[];
}

export interface ISetUserRolesAction extends TAction<typeof SET_USER_ROLES_ACTION> {
  userRoles: TUserRole[];
}

export interface ISetGroupsLoadedAction extends TAction<typeof SET_GROUPS_LOADED> {
  groupsLoaded: boolean;
}

export interface IResetModelsAction extends TAction<typeof RESET_MODELS_ACTION> {
}

export type IModelsActions = ISetGroups
  | IAddGroup
  | IRefreshCallGroupAction
  | IRemoveGroup
  | IAddAudioInGroup
  | IUpdateAudioAction
  | IMoveAudioToGroup
  | IRemoveAudioFromGroup
  | ISetLanguages
  | ISetActiveGroupId
  | ISetLangsAction
  | ISetUserRolesAction
  | ISetGroupsLoadedAction
  | IGroupsLoadedAction
  | IResetModelsAction
  ;

//**************** MODEL TYPES ********************/

export type TLanguageCode = string;

export type TUserLanguage = {
  name: string;
  code: TLanguageCode;
  status?: ELanguageStatus;
};

export type TLanguage = {
  id: number;
  name: string;
  code: TLanguageCode;
  installUserGroupExist?: boolean
  contentLibVideosExist: boolean
};

export enum TAudioType {
	AUDIO = 0, TEXT = 1, CONFERENCE = 2,
}

export type TConferenceProgressPhase = // from backend
    | 'WAITING_FILES'
    | 'REMUXING'
    | 'MERGING'
    | 'UPLOADING'
    | 'BUILDING_VIDEO'
    | 'TRANSCRIBING'
    | 'MERGING_CAPTIONS'
    | 'SAVING_CAPTIONS'
    | 'GENERATING_INFO' // AI title/description
    | 'DONE'
    | 'FAILED'
    | 'CANCELLED';

export type TConferenceProgress = {
    phase: TConferenceProgressPhase;
    processedFiles: number;             // progress inside the current loop (remux / transcribe)
    totalFiles: number;
    expectedFiles: number;              // how many audio files we expect from participants
    foundFiles: number;                 // how many already appeared in S3
    missingUserIds: string[];           // serviceUserIds not yet uploaded
    complete: boolean;                  // did ALL expected files arrive
    captionsCount?: number;
    durationSec?: number;
    startedAt: number;
    updatedAt: number;
    error?: string;
    keptExisting?: boolean;             // reprocess kept the previous (better) audio, built nothing new
    filesFinal?: boolean;               // no more files will arrive (reprocess, or the call is long over)
};

export type TAudio = {
  id: number;
  title: string;
  description?: string | null; // call recordings: AI-generated or the user's own edit
  // call recordings: the AI title/description in the user's native language; hasOwn* = the field
  // above is the user's own edit, shown in both target and native modes
  titleNative?: string | null;
  descriptionNative?: string | null;
  hasOwnTitle?: boolean;
  hasOwnDescription?: boolean;
  key: string;
  url: string;
  createdAt: string;
  language?: TLanguage;
  savePhraseCount: number;
  translatePhraseCount: number;
  watchPercent: number;
  type: TAudioType;
  position: number;
  orderNum: number;
  duration?: number;
  isLoading?: boolean;
  audioS3FilePath?: string;
  unprocessed?: boolean;
  progress?: TConferenceProgress;
  progressLog?: string[];
  progressStartedAt?: number;     // client clock when the run began (total timer baseline)
  progressUpdatedAt?: number;     // client clock of the last snapshot (freezes timers at the end)
  progressStepStartedAt?: number; // client clock when the current step began (step timer baseline)
  captionsCount?: number;
  exercisesTotal?: number; // active exercises in the recording (absent = none)
  exercisesDone?: number; // of those, answered in full by the current user
  langId?: number; // conference setting
  multilingual?: boolean; // conference setting
  transcriptionProvider?: string | null; // conference setting
  saveLevel?: TConferenceSaveLevel; // conference setting
  // Conference audio only: its Conference row's roomName — a stable id, unlike parsing the room out
  // of audioS3FilePath, which a media deletion can clear. Prefer RecordingsEffects.getRoomIdForAudio().
  roomName?: string;
  // The caller's endless-book book made from this recording (shown on the Book tab); null until created.
  bookId?: number | null;
  // Transient client-only flag: a saveLevel change is in flight (the backend synchronously deletes
  // media/raw sources for a downgrade, so this isn't instant) — drives a loader on the tile.
  savingSaveLevel?: boolean;
  // Transient client-only flag: a manual "Delete audio"/"Delete video" request is in flight — same
  // synchronous S3/raw-source deletion cost, same loader.
  deletingMedia?: boolean;
  // Transient client-only flag: the call just ended and /process is in flight for this room's
  // "Unprocessed call" placeholder — drives a loader so it doesn't sit static while waiting.
  finishingCall?: boolean;
  isConfOwner?: boolean;
  // conference audio only: set when the room had a cam-video track — open the recording as video.
  videoUrl?: string | null;
}

export type TParticipant = {
  id: number;
  name: string;
  picture?: string;
}

export type TRecording = TAudio & {
  participants: TParticipant[];
};

export type TUserGroupBase = {
  id: number;
  name: string;
}

export type TUserGroup = TUserGroupBase & {
  ownerId: number;
  phrasesCount: number;
  userPhrasesCount: number;
  languages: TUserLanguage[];
  audios: TAudio[];
  description: string;
  public: boolean;
  level: EUserLevel;
  type: EUserGroupType;
  srcCourseId?: number;
  watchPercent?: number;
}

export const EN_LANG_CODE = 'en';
export const EN_AUTO_GEN_LANG_CODE = 'en_autogen';

export enum EUserRoleId {
  Teacher = 1,
  Influencer = 2,
  Polyglot = 3,
  Student = 4
}

export type TUserRole = {
  id: number;
  name: string;
}
